import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { RouteComponentProps, withRouter } from 'react-router';
import { ConnectionStep } from '../steps/ConnectionStep';
import { FileHandlingStep } from '../steps/FileHandlingStep';
import { ImportType, DataType, CalculationModeType, InventoryIdentificationMatchMode, SupplierIdentificationMatchMode, LibraryImportExistingInventoryModeType, ImportHierarchyOption, LibraryImportNonSummaryRowsModeType, PostImportActionEnum, SftpImportConfigurationWorkflow, WorkflowStep, InventoryMode, FrequencyEnum } from '../../../store/Models/Api/SftpImportModels';
import ReviewStep from '../steps/ReviewStep';
import { ScheduleStep } from '../steps/ScheduleStep';
import ColumnMappingStep from '../steps/ColumnMappingStep';
import { DefaultHandlingStep } from '../steps/DefaultHandlingStep';
import { ConfigureLevelsStep } from '../steps/ConfigureLevelsStep';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../../store/Models/UserContext';
import { AppServices } from '../../../AppServices';
import Layout from '../../Layout';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { WorkflowStepper } from './WorkflowStepper';
import { SuccessToast } from '../components/SuccessToast';
import { ValidationErrorPopup } from '../components/ValidationErrorPopup';
import { FileHelper } from '../../../utils/FileHelper';
import { ColumnMappingHelper } from '../helpers/ColumnMappingHelper';

const SFTP_CONFIG_LIMIT = 5;

type SftpConfigurationWorkflowLocalProps = RouteComponentProps<{ configId?: string }>;

interface SftpConfigurationWorkflowStateProps {
    appServices: AppServices;
}

type SftpConfigurationWorkflowProps = SftpConfigurationWorkflowLocalProps & SftpConfigurationWorkflowStateProps;

// Using SftpConfigurationContext from SftpImportModels instead of custom interface

interface SftpConfigurationWorkflowState {
    currentStep: WorkflowStep;
    maxStepReached: WorkflowStep;
    configData: SftpImportConfigurationWorkflow;    
    connectionData: {
        host: string;
        username: string;
        sshKey: string;
        directoryPath: string;
    } | null;
    showSuccessToast: boolean;
    showValidationError: boolean;
    validationErrorMessage: string;
    toastMessage: string;
    isLoading: boolean;
    errorMessage: string;
    isSaving: boolean;
    configCount: number;
    isLimitReached: boolean;
}

class SftpConfigurationWorkflow extends ToxnotSignInRequiredComponent<SftpConfigurationWorkflowProps, SftpConfigurationWorkflowState> {
    private serviceClient = new ToxnotServiceClient();
    private sectionRef = React.createRef<HTMLDivElement>();
    
    constructor(props: SftpConfigurationWorkflowProps) {
        super(props);
        this.state = {
            currentStep: WorkflowStep.Connection,
            maxStepReached: WorkflowStep.Connection,
            configData: {
                configurationName: '',
                fileName: '',
                subPath: '',
                spreadsheetHeadersToColumnIdMappings: [],
                postImportAction: PostImportActionEnum.Skip,
                frequency: FrequencyEnum.Daily,
                startTime: '02:00',
                timeZone: 'EST',
                importType: undefined,
                inventoryMode: undefined,
                dataType: undefined,
                hasHeader: true,
                headerEndRow: 1,
                hasFooter: false,
                footerStartRow: undefined,                
                sheetName: '',
                tags: [],
                calculationMode: CalculationModeType.ToxnotStandardV2,
                shouldExtractAndLink: true,
                shouldExtractLeafiestMaterials: true,
                extractAndLinkShouldOverwriteSubmaterials: true,
                shouldMatchMaterials: true,
                inventoryIdentificationMatchMode: InventoryIdentificationMatchMode.Default,
                supplierIdentificationMatchMode: SupplierIdentificationMatchMode.Default,
                libraryImportExistingInventoryMode: LibraryImportExistingInventoryModeType.OverwriteExisting,
                libraryImportShouldUpdateSummaryRows: true,
                libraryImportNonSummaryRowsMode: LibraryImportNonSummaryRowsModeType.ReplaceNonSummaryRows,
                importHierarchyOptionType: ImportHierarchyOption.LevelColumn,
                isActive: true
            },
            connectionData: null,
            showSuccessToast: false,
            showValidationError: false,
            validationErrorMessage: '',
            toastMessage: '',
            isLoading: false,
            errorMessage: '',
            isSaving: false,
            configCount: 0,
            isLimitReached: false
        };
    }
    
    public async componentDidMount() {
        const { configId } = this.props.match.params;

        if (configId) {
            // Edit mode: Fetch existing config data
            this.setState({ isLoading: true, errorMessage: '' });
            
            try {
                const response = await this.serviceClient.sftpImportConfigurationGet(configId);
                
                if (response.success && response.data) {
                    const data = { ...response.data } as SftpImportConfigurationWorkflow;

                    this.setState({ 
                        configData: data,
                        maxStepReached: WorkflowStep.Review, 
                        isLoading: false 
                    });
                    
                    // Load connection data separately
                    await this.loadConnectionData();
                } else {
                    this.setState({ 
                        errorMessage: this.localizeString('Failed to load configuration. Please try again.'),
                        isLoading: false 
                    });
                }
            } catch (error) {
                this.setState({ 
                    errorMessage: this.localizeString('Error loading configuration: ') + (error as Error).message,
                    isLoading: false 
                });
            }
        } else {
            // Add mode: Set defaults and load connection data
            this.setState({ isLoading: false });
            await this.loadConnectionData();
            
            // Check configuration limit for new configurations
            await this.checkConfigurationLimit();
        }
    }

    public componentDidUpdate(prevProps: SftpConfigurationWorkflowProps, prevState: SftpConfigurationWorkflowState) {
        // Scroll to top when step changes
        if (prevState.currentStep !== this.state.currentStep) {
            this.sectionRef.current?.scrollTo(0, 0);
        }
    }

    protected getDisallowedRoles(): TeamRole[] {
        // Only allow editors and admins
        return [TeamRole.Viewer, TeamRole.Marketing];
    }

    private checkConfigurationLimit = async (): Promise<boolean> => {
        try {
            const response = await this.serviceClient.sftpConfigurationsGetAllForAccount(true);
            
            if (response.success && response.data) {
                const configCount = response.data.length;
                const isLimitReached = configCount >= SFTP_CONFIG_LIMIT;
                
                this.setState({ 
                    configCount,
                    isLimitReached 
                });
                
                return isLimitReached;
            }
            
            return false;
        } catch (error) {
            return false;
        }
    }

    private getSteps = (): WorkflowStep[] => {
        const { configData } = this.state;
        let allSteps: WorkflowStep[] = [WorkflowStep.Connection, WorkflowStep.FileHandling, WorkflowStep.DefaultHandling, WorkflowStep.ConfigureLevels, WorkflowStep.ColumnMapping, WorkflowStep.Schedule, WorkflowStep.Review];

        if (!this.isDefaultHandlingEnabled()) {
            allSteps = allSteps.filter(s => s !== WorkflowStep.DefaultHandling);
        }

        if (configData.dataType !== DataType.Bom) {
            allSteps = allSteps.filter(s => s !== WorkflowStep.ConfigureLevels);
        }

        if (configData.dataType === DataType.Summary || configData.dataType === DataType.Scip || configData.dataType === DataType.ExternalDbIdUpdate) {
            allSteps = allSteps.filter(s => s !== WorkflowStep.ColumnMapping);
        }

        return allSteps;
    }

    private setStep = (step: WorkflowStep) => {
        const steps = this.getSteps();
        let maxStep = this.state.maxStepReached;
        const maxStepIndex = steps.indexOf(this.state.maxStepReached);
        const currentStepIndex = steps.indexOf(step);

        if (currentStepIndex > maxStepIndex) {
            maxStep = steps[currentStepIndex];
        }        this.setState({ currentStep: step, maxStepReached: maxStep });
    }

    private handleConfigChange = (data: Partial<SftpImportConfigurationWorkflow>) => {
        this.setState(prevState => ({
            configData: {
                ...prevState.configData,
                ...data
            }
        }));
    }

    private isDefaultHandlingEnabled = (): boolean => {
        const { configData } = this.state;
        return ((configData.importType === ImportType.Products || configData.importType === ImportType.Materials) && configData.dataType === DataType.Bom) || configData.importType === ImportType.Suppliers;
    }

    private isCurrentStepValid = (): boolean => {
        const { currentStep, configData } = this.state;
        if (currentStep === WorkflowStep.Connection) {
            return configData.fileName.trim() !== '';
        }
        if (currentStep === WorkflowStep.FileHandling) {
            return configData.importType === ImportType.Suppliers || configData.dataType === DataType.Scip || configData.dataType === DataType.Summary || configData.dataType === DataType.ExternalDbIdUpdate || (configData.importType != null && configData.inventoryMode != null && configData.dataType !== null);
        }
            if (currentStep === WorkflowStep.DefaultHandling) {
                if (configData.inventoryMode === InventoryMode.One && (!configData.name || configData.name.trim() === "")) {
                return false;
            }
            // Check if header is enabled but headerEndRow is empty or invalid
            if (configData.hasHeader && (configData.headerEndRow === undefined || configData.headerEndRow <= 0)) {
                return false;
            }
            // Check if footer is enabled but footerStartRow is empty or invalid
            if (configData.hasFooter && (configData.footerStartRow === undefined || configData.footerStartRow <= 0)) {
                return false;
            }
            return true;
        }
        if (currentStep === WorkflowStep.Schedule) {
            return configData.postImportAction !== null && configData.frequency !== null && configData.startTime.trim() !== '' && (configData.timeZone || '').trim() !== '';
        }
        if (currentStep === WorkflowStep.ColumnMapping) {
            return configData.spreadsheetHeadersToColumnIdMappings.every(mapping =>
                mapping.headerName.trim() !== '' && mapping.columnId.trim() !== ''
            );
        }
        return true;
    }

    
    private closeValidationPopup = () => {
        
        this.setState({
            showValidationError: false,
            validationErrorMessage: ''
        });
    };

    private validateAndHandleSave = async () => {
            const { configData } = this.state;
            const { fileName, subPath } = configData;
            
            const pathValidation = FileHelper.validateFilePathCombination(fileName, subPath);
            
            if (!pathValidation.isValid) {
                this.setState({ 
                    showValidationError: true,
                    validationErrorMessage: pathValidation.errorMessage || this.localizeString('Invalid file path.')
                });
                return;
            }
            
            //Skip any further validation for scip and summary
            const skipsColumnMapping = configData.dataType === DataType.Scip || configData.dataType === DataType.Summary || configData.dataType === DataType.ExternalDbIdUpdate;
            if (skipsColumnMapping) {
                this.handleSaveConfiguration();
                return;
            }

            // Use the centralized validator
            const shouldValidateBom = configData.importType === ImportType.Products || configData.importType === ImportType.Materials;
            const validationResult = ColumnMappingHelper.validateAll(configData, shouldValidateBom);

            if (!validationResult.isValid) {
                this.setState({
                    showValidationError: true,
                    validationErrorMessage: this.localizeString(validationResult.errorMessage!)
                });
                return;
            }

            if(shouldValidateBom){
                // Check if external Db Id is required and skipped
                const notExternalDbIdResult = await ColumnMappingHelper
                    .validateExternalDbIdForMapping(configData.spreadsheetHeadersToColumnIdMappings);

                if (!notExternalDbIdResult.isValid) {
                    this.setState({
                        showValidationError: true,
                        validationErrorMessage: this.localizeString(notExternalDbIdResult.errorMessage!)
                    });
                    return;
                }
            }

            // All validations passed, proceed to next step
            this.handleSaveConfiguration();
        };

    private handleSaveConfiguration = async () => {
        const { configId } = this.props.match.params;

        // For new configurations, check if limit would be exceeded
        if (!configId) {
            const isLimitReached = await this.checkConfigurationLimit();
            if (isLimitReached) {
                this.setState({ 
                    showValidationError: true,
                    validationErrorMessage: this.localizeString(`You have reached the maximum limit of ${SFTP_CONFIG_LIMIT} SFTP configurations. Please delete an existing configuration before creating a new one.`)
                });
                return;
            }
        }

        this.setState({ isSaving: true, errorMessage: '' });

        try {
            // Set the configId if we're editing
            const workflowData: SftpImportConfigurationWorkflow = {
                ...this.state.configData,
                configId: configId || undefined
            };

            let response;
            if (configId) {
                response = await this.serviceClient.sftpImportConfigurationUpdate(workflowData);
            } else {
                response = await this.serviceClient.sftpImportConfigurationCreate(workflowData);
            }

            if (response.success) {
                const message = configId ? this.localizeString("Configuration updated successfully!") : this.localizeString("Configuration created successfully!");
                this.setState({ 
                    showSuccessToast: true, 
                    toastMessage: message,
                    isSaving: false 
                });

                setTimeout(() => {
                    this.setState({ showSuccessToast: false, toastMessage: '' });
                    window.location.href = '/Developer';
                }, 3000);
            } else {
                this.setState({ 
                    errorMessage: this.localizeString('Failed to save configuration. Please try again.'),
                    isSaving: false 
                });
            }
        } catch (error) {
            this.setState({ 
                errorMessage: this.localizeString('Error saving configuration: ') + (error as Error).message,
                isSaving: false 
            });
        }
    }

    private renderStep() {
        const { configData } = this.state;

        switch (this.state.currentStep) {
            case WorkflowStep.Connection:
                return <ConnectionStep
                    goToNextStep={() => this.setStep(WorkflowStep.FileHandling)}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                    connectionData={this.state.connectionData}
                />;
            case WorkflowStep.FileHandling:
                return <FileHandlingStep
                    goToPrevStep={() => this.setStep(WorkflowStep.Connection)}
                    goToNextStep={(skipDefaultHandling) => {
                        const nextStep = (this.isDefaultHandlingEnabled() && !skipDefaultHandling) ? WorkflowStep.DefaultHandling : WorkflowStep.ColumnMapping;
                        if (this.state.configData.dataType === DataType.Summary || this.state.configData.dataType === DataType.Scip || this.state.configData.dataType === DataType.ExternalDbIdUpdate) {
                            this.setStep(WorkflowStep.Schedule);
                        } else {
                            this.setStep(nextStep);
                        }
                    }}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                />;
            case WorkflowStep.DefaultHandling:
                return <DefaultHandlingStep
                    goToPrevStep={() => this.setStep(WorkflowStep.FileHandling)}
                    goToNextStep={() => {
                        if (this.state.configData.dataType === DataType.Summary || this.state.configData.dataType === DataType.Scip || this.state.configData.dataType === DataType.ExternalDbIdUpdate) {
                            this.setStep(WorkflowStep.Schedule);
                        } else if (this.state.configData.dataType === DataType.Bom) {
                            this.setStep(WorkflowStep.ConfigureLevels);
                        } else {
                            this.setStep(WorkflowStep.ColumnMapping);
                        }
                    }}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                />;
            case WorkflowStep.ConfigureLevels:
                return <ConfigureLevelsStep
                    goToPrevStep={() => this.setStep(WorkflowStep.DefaultHandling)}
                    goToNextStep={() => this.setStep(WorkflowStep.ColumnMapping)}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                />;
            case WorkflowStep.ColumnMapping:
                return <ColumnMappingStep
                    goToPrevStep={() => {
                        if (this.state.configData.importType === ImportType.Suppliers) {
                            this.setStep(WorkflowStep.DefaultHandling);
                        } else {
                            this.setStep(this.isDefaultHandlingEnabled() ? WorkflowStep.ConfigureLevels : WorkflowStep.FileHandling);
                        }
                    }}
                    goToNextStep={() => this.setStep(WorkflowStep.Schedule)}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                />;
            case WorkflowStep.Schedule:
                return <ScheduleStep
                    goToPrevStep={() => {
                        if (this.state.configData.dataType === DataType.Summary || this.state.configData.dataType === DataType.Scip || this.state.configData.dataType === DataType.ExternalDbIdUpdate) {
                            this.setStep(WorkflowStep.FileHandling);
                        } else {
                            this.setStep(WorkflowStep.ColumnMapping);
                        }
                    }}
                    goToNextStep={() => this.setStep(WorkflowStep.Review)}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                />;
            case WorkflowStep.Review:
                const { configId } = this.props.match.params;
                return <ReviewStep
                    goToPrevStep={() => this.setStep(WorkflowStep.Schedule)}
                    configData={configData}
                    onConfigChange={this.handleConfigChange}
                    onSave={this.validateAndHandleSave}
                    connectionData={this.state.connectionData}
                    isLimitReached={this.state.isLimitReached}
                    configId={configId}
                />;
            default:
                return null;
        }
    }

    protected renderConcreteComponent() {
        const steps = this.getSteps();

        return (
            <Layout options={{ pinAppContentToWindowHeight: true }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div id="tow-root" style={{ flexShrink: 0 }}>
                        <svg style={{ display: "none" }}>
                            <symbol id="step-active-neither">
                                <polyline points="0,0 9,12 0,25" style={{ fill: "#fff", stroke: "rgba(0,0,0,0.1)" }}></polyline>
                            </symbol>
                            <symbol id="step-active-left">
                                    <polygon points="0,0 9,12 0,25" style={{ fill: "#5cb85c" }}></polygon>
                                    <polyline points="0,0 9,12 0,25" style={{ fill: "#5cb85c", stroke: "rgba(0,0,0,0.1)" }}></polyline>
                            </symbol>
                            <symbol id="step-active-right">
                                    <polygon points="0,0 9,12 0,25 10,25 10,0" style={{ fill:"#42af3d"}}></polygon>
                                    <polyline points="0,0 9,12 0,25" style={{ fill: "#fff", stroke:"#42af3d"}}></polyline>
                            </symbol>
                        </svg>

                    <div id="tow-active-workflow-container">
                        <div className="tow-workflow-content">
                            <div className="tow-workflow-nav">
                                <div className="tow-title">
                                    {this.localizeString("SFTP Configuration")}
                                </div>
                                <WorkflowStepper
                                    steps={steps}
                                    currentStep={this.state.currentStep}
                                    maxStepReached={this.state.maxStepReached}
                                    isCurrentStepValid={this.isCurrentStepValid}
                                    setStep={this.setStep}
                                />
                                </div>
                        </div>
                    </div>
                </div>
                    <div 
                        className="tn-section" 
                        style={{ flex: '1 1 auto', overflowY: 'auto', padding: '15px' }}
                        ref={this.sectionRef}
                    >
                        {this.renderStep()}
                    </div>
                <SuccessToast show={this.state.showSuccessToast} message={this.state.toastMessage} />
                <ValidationErrorPopup 
                    show={this.state.showValidationError} 
                    message={this.state.validationErrorMessage} 
                    onClose={this.closeValidationPopup}
                />
                </div>
            </Layout>
        );
    }    private loadConnectionData = async () => {
        try {
            const response = await this.serviceClient.sftpClientConfigurationGetByOwner(false);
              if (response.success && response.data) {
                // Map the connection data from DbClientSftpConfiguration
                this.setState({
                    connectionData: {
                        host: `${response.data.storageAccountName}.blob.core.windows.net` || 'Not configured',
                        username: response.data.storageUserName || 'Not configured',
                        sshKey: response.data.privateKey || 'Not configured',
                        directoryPath: response.data.directoryPath || 'Not configured'
                    }
                });
            }
        } catch (error) {
            this.setState({
                connectionData: {
                    host: 'Connection info not available',
                    username: 'Connection info not available',
                    sshKey: 'Connection info not available',
                    directoryPath: 'Connection info not available'
                }
            });
        }
    };
}

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices }),
    null
)(withRouter(SftpConfigurationWorkflow));
